import React, { FC } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { AddIcon, Switch, SwitchButtons, TrashIcon } from 'src/shared/components';
import { AvailabilityStatusEnums, OperationTypeEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatTime, getTimeFromSeconds } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

import { IAvailabilityForm } from './availabilities.types';
import { getStatusColor, mapAvailabilityStatusEnum } from './availabilities.utils';
import { EmployeeAvailabilityTimeTextField } from './EmployeeAvailabilityTimeTextField';

interface Props {
  availabilities: IAvailabilityForm[];
  isEditMode: boolean;
  isFullDay: boolean;
  onChangeFullDay: (wasAllDay: boolean) => void;
  onChangeStatus: (status: AvailabilityStatusEnums, index: number) => void;
  onDelete: (index: number) => void;
  onAdd: () => void;
  onChangeStartTime: (value: string | undefined, index: number) => void;
  onChangeEndTime: (value: string | undefined, index: number) => void;
  getErrors: (index: number, key: keyof IAvailabilityForm) => string | undefined;
  getTouched: (index: number) => boolean;
  disabled?: boolean;
}

export const EmployeeAvailabilityEditTable: FC<Props> = ({
  availabilities,
  isEditMode,
  isFullDay,
  onChangeFullDay,
  onChangeStatus,
  onDelete,
  onAdd,
  onChangeStartTime,
  onChangeEndTime,
  getErrors,
  getTouched,
  disabled,
}) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);

  const filteredAvailabilities = availabilities.filter((av) => av.operationType !== OperationTypeEnums.DELETE);

  const disableDelete = disabled || filteredAvailabilities.length <= 1;

  return (
    <>
      <Stack direction="column" paddingX={1} gap={2} mb={2}>
        <Typography variant="body2" fontWeight={500}>
          {t('availability.availabilityType')}
        </Typography>

        {isEditMode ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <Switch
              id="overrides-status-toggle-btn"
              checked={!isFullDay}
              onChange={() => onChangeFullDay(isFullDay)}
              disabled={disabled}
            />

            <Typography variant="body2">{t('availability.timeRange')}</Typography>
          </Stack>
        ) : (
          <>
            <Typography variant="body2">
              {isFullDay ? t('availability.allDay') : t('availability.timeRange')}
            </Typography>
          </>
        )}
      </Stack>
      <TableContainer sx={{ border: 'none', 'td, th': { border: 'none', p: '0.5rem !important' } }}>
        <Table padding="none" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width="40%">{t('global.availability')}</TableCell>
              {!isFullDay ? (
                <>
                  <TableCell width="20%">
                    <label className={isEditMode ? 'required' : undefined}>{t('global.start')}</label>
                  </TableCell>
                  <TableCell width="20%">
                    <label className={isEditMode ? 'required' : undefined}> {t('global.end')}</label>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell />
                  <TableCell />
                </>
              )}

              <TableCell width="20%" />
            </TableRow>
          </TableHead>
          <TableBody sx={{ td: { verticalAlign: 'top !important' } }}>
            {availabilities.map((item, index, list) => {
              const { status, startTimeInSeconds, endTimeInSeconds, operationType } = item;

              // Avoid rendering deleted overrides
              if (operationType === OperationTypeEnums.DELETE) {
                return null;
              }

              const startTime = getTimeFromSeconds(startTimeInSeconds);
              const endTime = getTimeFromSeconds(endTimeInSeconds);

              const isLastIndex = index === list.length - 1 || filteredAvailabilities.length - 1 <= 0;

              if (isEditMode) {
                const touched = getTouched(index);

                return (
                  <TableRow key={`item-${index}`}>
                    <TableCell>
                      <SwitchButtons
                        onToggle={() => onChangeStatus(status, index)}
                        toggle={status === AvailabilityStatusEnums.AVAILABLE}
                        leftContent={t('availability.available')}
                        rightContent={t('availability.unavailable')}
                      />
                    </TableCell>

                    {!isFullDay && (
                      <>
                        <TableCell>
                          <EmployeeAvailabilityTimeTextField
                            value={startTime}
                            onInput={(value) => onChangeStartTime(value, index)}
                            error={touched ? getErrors(index, 'startTimeInSeconds') : undefined}
                            disabled={disabled}
                          />
                        </TableCell>
                        <TableCell>
                          <EmployeeAvailabilityTimeTextField
                            value={endTime}
                            onInput={(value) => onChangeEndTime(value, index)}
                            error={touched ? getErrors(index, 'endTimeInSeconds') : undefined}
                            disabled={disabled}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {!isFullDay && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <IconButton disabled={disableDelete} color="error" onClick={() => onDelete(index)}>
                            <TrashIcon size={20} />
                          </IconButton>
                          {isLastIndex && (
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<AddIcon />}
                              onClick={onAdd}
                              classes={{ startIcon: 'm-0' }}
                              sx={{ minWidth: 'unset', width: '28px', height: '28px', minHeight: '28px', p: 0 }}
                              disabled={disabled}
                            />
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow key={`availability-${index}`}>
                  <TableCell
                    sx={{
                      color: (theme) => getStatusColor(theme, status),
                    }}
                  >
                    {mapAvailabilityStatusEnum(status, t)}
                  </TableCell>
                  {startTime !== null && endTime !== null ? (
                    <>
                      <TableCell
                        sx={{
                          color: (theme) => getStatusColor(theme, status),
                        }}
                      >
                        {formatTime(startTime, timeFormat)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: (theme) => getStatusColor(theme, status),
                        }}
                      >
                        {formatTime(endTime, timeFormat)}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell />
                      <TableCell />
                    </>
                  )}
                  <TableCell />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
